import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ApnsConfiguration } from "@/types/apns";
import { ApnsConfigurationCard } from "./ApnsConfigurationCard";
import { ApnsConfigurationDialog } from "./ApnsConfigurationDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, Shield } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function ApnsConfigurationList() {
  const { toast } = useToast();
  const [configurations, setConfigurations] = useState<ApnsConfiguration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ApnsConfiguration | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<ApnsConfiguration | null>(null);

  const loadConfigurations = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('apns_configurations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setConfigurations(data || []);
    } catch (error: any) {
      console.error('Error loading APNs configurations:', error);
      toast({
        title: "Error",
        description: "Failed to load APNs configurations",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadConfigurations();
  }, []);

  const handleAdd = () => {
    setEditingConfig(null);
    setDialogOpen(true);
  };

  const handleEdit = (config: ApnsConfiguration) => {
    setEditingConfig(config);
    setDialogOpen(true);
  };

  const handleDeleteClick = (config: ApnsConfiguration) => {
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!configToDelete) return;

    try {
      const { error } = await supabase
        .from('apns_configurations')
        .delete()
        .eq('id', configToDelete.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "APNs configuration deleted successfully",
      });
      
      loadConfigurations();
    } catch (error: any) {
      console.error('Error deleting configuration:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete configuration",
        variant: "destructive"
      });
    } finally {
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Loading APNs configurations...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">APNs Certificates</h3>
          <p className="text-sm text-muted-foreground">
            Manage Apple Push Notification service certificates for your organization
          </p>
        </div>
        <Button type="button" onClick={handleAdd} className="gap-2">
          <Plus className="h-4 w-4" />
          Add APNs Configuration
        </Button>
      </div>

      {configurations.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-12 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
              <Shield className="w-8 h-8 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">No APNs Certificates Configured</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                APNs certificates are required for sending push notifications to Apple devices.
                Get started by adding your first configuration.
              </p>
            </div>
            <Button type="button" onClick={handleAdd} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Your First APNs Configuration
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {configurations.map((config) => (
            <ApnsConfigurationCard
              key={config.id}
              config={config}
              onEdit={() => handleEdit(config)}
              onDelete={() => handleDeleteClick(config)}
            />
          ))}
        </div>
      )}

      <ApnsConfigurationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={loadConfigurations}
        initialData={editingConfig}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete APNs Configuration?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{configToDelete?.name}"? This action cannot be undone.
              Devices using this certificate will no longer receive push notifications.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
